/**
 * Amount-first payment allocation (GDN-2400, Option B).
 *
 * Pure math for the settle sheet's payment methods (cash / external): given
 * an amount and an ordered list of open targets — the eligibility-suggested
 * copay for the in-scope encounter first, then open charge lines oldest
 * first — waterfall the money down the list. Whatever is left is NEVER an
 * error: it becomes account credit.
 *
 * Production port of `prototypes/option-b/allocationEngine.ts`. Targets are
 * built from the drawer's real preview groups + eligibility snapshot, and
 * `buildRecordPaymentRequests` maps a computed allocation onto the existing
 * `POST /billing/patients/{id}/payments` request shape (charge_line_ids
 * partial allocation, encounter_id linkage, create_charge copay macro) —
 * splitting into one request per portion when a single call can't express
 * the whole allocation.
 */

import type { DisplayEncounterChargeGroup } from '@features/billing/components/charge-groups';

/** Eligibility-suggested copay, threaded from the drawer's snapshot. */
export interface CopaySuggestion {
  amountCents: number;
  /** Plan/payer display name when the API exposes one (it currently doesn't). */
  planName?: string | null;
  /** ISO timestamp of the eligibility check that produced the suggestion. */
  checkedAt?: string | null;
}

export interface AllocationTarget {
  id: string;
  /** e.g. "Copay — this visit" or the charge line's responsibility label. */
  title: string;
  /** e.g. "Jul 13 · Psychotherapy, 45 min · CPT 90834" */
  subtitle: string;
  dueCents: number;
  kind: 'copay' | 'charge';
  /** Encounter the target belongs to (drives encounter_id linkage). */
  encounterId?: string | null;
  /** Real charge line id — absent for the synthetic copay target. */
  chargeLineId?: string | null;
  /** Set when the due amount came from an eligibility check. */
  suggestedFromCoverage?: boolean;
}

export interface AllocationLine {
  target: AllocationTarget;
  /** Cents applied to this target by the waterfall (0 when excluded). */
  appliedCents: number;
  included: boolean;
}

export interface AllocationResult {
  amountCents: number;
  lines: AllocationLine[];
  allocatedCents: number;
  /** Remainder → account credit. Always allowed, never blocks. */
  creditCents: number;
}

/**
 * Waterfall allocation: fill included targets in order, remainder → credit.
 * Deliberately has no failure mode — any non-negative amount produces a
 * valid, fully-described outcome.
 */
export function computeAllocation(
  amountCents: number,
  targets: AllocationTarget[],
  excludedIds: ReadonlySet<string>,
): AllocationResult {
  const amount = Math.max(0, Math.round(amountCents));
  let remaining = amount;
  const lines: AllocationLine[] = targets.map(target => {
    const included = !excludedIds.has(target.id);
    const appliedCents = included ? Math.min(remaining, target.dueCents) : 0;
    remaining -= appliedCents;
    return { target, appliedCents, included };
  });
  return {
    amountCents: amount,
    lines,
    allocatedCents: amount - remaining,
    creditCents: remaining,
  };
}

export function parseAmountToCents(raw: string): number {
  const parsed = Number.parseFloat(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.round(parsed * 100);
}

interface BuildTargetsArgs {
  /** Preview-derived encounter/charge-line groups from the drawer. */
  chargeGroups: DisplayEncounterChargeGroup[];
  /** Charge lines the payment endpoint may allocate against. */
  selectableChargeLineIds: string[];
  /** Eligibility-suggested copay for the in-scope encounter, if known. */
  copaySuggestion?: CopaySuggestion | null;
  /** Encounter scope — required for the synthetic copay target. */
  encounterId?: string | null;
}

/**
 * Target ordering per the Option B spec: the eligibility-suggested copay for
 * the in-scope encounter first, then open charge lines oldest first.
 *
 * The synthetic copay target only exists when the in-scope encounter has no
 * open charge line of its own — once a real line lands, the line IS the
 * collectable and a second copay row would double-count.
 */
export function buildPaymentAllocationTargets({
  chargeGroups,
  selectableChargeLineIds,
  copaySuggestion,
  encounterId,
}: BuildTargetsArgs): AllocationTarget[] {
  const selectable = new Set(selectableChargeLineIds);
  const chargeTargets: AllocationTarget[] = [];
  for (const group of chargeGroups) {
    for (const line of group.lines) {
      if (!selectable.has(line.id) || line.amountCents <= 0) continue;
      chargeTargets.push({
        id: line.id,
        title: chargeLineTitle(line),
        subtitle: chargeLineSubtitle(group, line),
        dueCents: line.amountCents,
        kind: 'charge',
        encounterId: group.id,
        chargeLineId: line.id,
      });
    }
  }
  // Oldest service date first; undated groups sink to the end.
  chargeTargets.sort((a, b) =>
    (serviceDateFor(chargeGroups, a) ?? '9999-99-99').localeCompare(
      serviceDateFor(chargeGroups, b) ?? '9999-99-99',
    ),
  );

  const copayTarget =
    encounterId &&
    copaySuggestion &&
    copaySuggestion.amountCents > 0 &&
    !chargeTargets.some(target => target.encounterId === encounterId)
      ? buildCopayTarget(copaySuggestion, encounterId)
      : null;

  return copayTarget ? [copayTarget, ...chargeTargets] : chargeTargets;
}

interface BuildCopayDeclarationArgs {
  /** Preview-derived encounter/charge-line groups from the drawer. */
  chargeGroups: DisplayEncounterChargeGroup[];
  /** Charge lines the payment endpoint may allocate against. */
  selectableChargeLineIds: string[];
  /** The visit the user declared this co-pay against. */
  declaredEncounterId: string;
  /** Eligibility-suggested copay (plan-level) when coverage knows it. */
  copaySuggestion?: CopaySuggestion | null;
  /** Entered amount — the declared copay when coverage has no suggestion. */
  amountCents: number;
}

/**
 * Targets for the explicit copay declaration (GDN-2405): the user said
 * "this money is a co-pay for visit X", so X collects first regardless of
 * service-date age — then any over-amount waterfalls into other visits'
 * open lines (the existing `buildRecordPaymentRequests` split), and the
 * remainder becomes account credit.
 *
 * The declared copay's due amount is the eligibility suggestion when
 * coverage knows it; otherwise the entered amount IS the declaration —
 * exact by construction, so it maps onto the create_charge macro.
 *
 * Same double-count rule as the general builder: once the declared visit
 * has an open charge line of its own, the line IS the collectable and no
 * synthetic copay target is added.
 */
export function buildCopayDeclarationTargets({
  chargeGroups,
  selectableChargeLineIds,
  declaredEncounterId,
  copaySuggestion,
  amountCents,
}: BuildCopayDeclarationArgs): AllocationTarget[] {
  const lineTargets = buildPaymentAllocationTargets({
    chargeGroups,
    selectableChargeLineIds,
    encounterId: null,
  });
  const declaredLines = lineTargets.filter(
    target => target.encounterId === declaredEncounterId,
  );
  const otherLines = lineTargets.filter(
    target => target.encounterId !== declaredEncounterId,
  );

  if (declaredLines.length > 0) return [...declaredLines, ...otherLines];

  const suggestedCents =
    copaySuggestion && copaySuggestion.amountCents > 0
      ? copaySuggestion.amountCents
      : null;
  const declaredDueCents = suggestedCents ?? Math.max(0, Math.round(amountCents));
  if (declaredDueCents <= 0) return otherLines;

  const copayTarget: AllocationTarget = {
    id: `copay:${declaredEncounterId}`,
    title: 'Copay — declared',
    subtitle: suggestedCents ? 'Suggested from coverage' : 'Entered manually',
    dueCents: declaredDueCents,
    kind: 'copay',
    encounterId: declaredEncounterId,
    suggestedFromCoverage: !!suggestedCents,
  };
  return [copayTarget, ...otherLines];
}

function buildCopayTarget(
  suggestion: CopaySuggestion,
  encounterId: string,
): AllocationTarget {
  return {
    id: `copay:${encounterId}`,
    title: 'Copay — this visit',
    subtitle: suggestion.planName
      ? `Suggested from coverage · ${suggestion.planName}`
      : 'Suggested from coverage',
    dueCents: suggestion.amountCents,
    kind: 'copay',
    encounterId,
    suggestedFromCoverage: true,
  };
}

/**
 * One recordPayment request derived from an allocation. A single collected
 * payment may map to MORE THAN ONE request (see `buildRecordPaymentRequests`)
 * because the BE contract cannot express a copay macro and charge-line
 * allocations in the same call.
 */
export interface RecordPaymentAllocationRequest {
  amount_cents: number;
  charge_line_ids: string[] | null;
  encounter_id: string | null;
  create_charge: true | undefined;
}

interface BuildFieldsArgs {
  isEncounterScope: boolean;
  encounterId?: string | null;
  method: 'cash' | 'external';
}

/**
 * Map an allocation onto the existing payments request(s). No new API
 * surface:
 *
 * - Applied charge lines → `charge_line_ids` (BE supports partial amounts).
 *   `encounter_id` rides along when the whole allocation lands on a single
 *   known encounter; lines spanning encounters send no linkage. The scope's
 *   encounter only fills in when the lines carry no encounter ids of their
 *   own — it must never contradict them.
 * - Money applied to the synthetic copay target with no credit remainder →
 *   the `create_charge=true` macro (mints a COPAY_ESTIMATED line for the
 *   amount and pays it — the eligibility collect→collected flip).
 * - Copay with an over-amount remainder → plain encounter-linked payment;
 *   the macro can't split, and the BE's widened ERA reconciliation
 *   (GDN-2399) nets encounter-linked payments and account credits later.
 * - Copay AND charge lines both applied → TWO requests. One request can't
 *   carry both: `create_charge` mints COPAY_ESTIMATED for the request's FULL
 *   `amount_cents`, and `charge_line_ids` caps the amount at the selected
 *   line balances. So the copay portion goes first under the pure-copay
 *   rules above (macro when exact, encounter-linked without the macro when
 *   credit rides along), then the charge portion for exactly its applied
 *   sum, linked to the lines' own encounter — never the copay's.
 * - External collections in encounter scope with no targets at all keep the
 *   legacy behavior: mint the charge + pay it in one call.
 * - Nothing applied → account-level credit (encounter-linked when scoped).
 */
export function buildRecordPaymentRequests(
  allocation: AllocationResult,
  { isEncounterScope, encounterId, method }: BuildFieldsArgs,
): RecordPaymentAllocationRequest[] {
  const scopedEncounterId = encounterId ?? null;
  const appliedCharges = allocation.lines.filter(
    line => line.target.kind === 'charge' && line.included && line.appliedCents > 0,
  );
  const copayLine = allocation.lines.find(
    line => line.target.kind === 'copay' && line.included && line.appliedCents > 0,
  );
  const hasAnyTargets = allocation.lines.length > 0;

  const chargeLinesRequest = (
    encounterLinkId: string | null,
    amountCents: number,
  ): RecordPaymentAllocationRequest => ({
    amount_cents: amountCents,
    charge_line_ids: appliedCharges.map(
      line => line.target.chargeLineId ?? line.target.id,
    ),
    encounter_id: encounterLinkId,
    create_charge: undefined,
  });

  const encounterIds = new Set(
    appliedCharges.map(line => line.target.encounterId ?? null),
  );
  const soleEncounterId =
    encounterIds.size === 1 ? Array.from(encounterIds)[0] : null;

  if (appliedCharges.length > 0 && copayLine) {
    // Mixed copay + charge lines: split into one request per portion. The
    // copay portion leads — it's today's collect→collected flip — and any
    // credit remainder rides with it (both are encounter-linked money that
    // GDN-2399 reconciliation nets), keeping the charge request's amount
    // exactly equal to its lines' applied sum.
    const copayEncounterId = copayLine.target.encounterId ?? scopedEncounterId;
    const copayRequest: RecordPaymentAllocationRequest =
      copayEncounterId && allocation.creditCents === 0
        ? {
            amount_cents: copayLine.appliedCents,
            charge_line_ids: null,
            encounter_id: copayEncounterId,
            create_charge: true,
          }
        : {
            amount_cents: copayLine.appliedCents + allocation.creditCents,
            charge_line_ids: null,
            encounter_id: copayEncounterId,
            create_charge: undefined,
          };
    const chargeAmountCents = appliedCharges.reduce(
      (total, line) => total + line.appliedCents,
      0,
    );
    return [copayRequest, chargeLinesRequest(soleEncounterId, chargeAmountCents)];
  }

  if (appliedCharges.length > 0) {
    // Invariant: a charge-line payment must never link to an encounter its
    // lines don't belong to. The lines' own encounters win over the sheet's
    // scope (a declared copay forces encounter scope even when the waterfall
    // overflows into OTHER visits' lines — GDN-2405): one known encounter →
    // link it, several → no linkage. The scope only fills in when the lines
    // carry no encounter information at all (legacy encounter-scope targets
    // without encounter ids).
    const linesCarryEncounterInfo = appliedCharges.some(
      line => line.target.encounterId != null,
    );
    const chargeLinkEncounterId = linesCarryEncounterInfo
      ? soleEncounterId
      : isEncounterScope
        ? scopedEncounterId
        : null;
    return [chargeLinesRequest(chargeLinkEncounterId, allocation.amountCents)];
  }

  if (copayLine && allocation.creditCents === 0 && scopedEncounterId) {
    return [
      {
        amount_cents: allocation.amountCents,
        charge_line_ids: null,
        encounter_id: scopedEncounterId,
        create_charge: true,
      },
    ];
  }

  if (
    method === 'external' &&
    isEncounterScope &&
    scopedEncounterId &&
    !hasAnyTargets &&
    allocation.amountCents > 0
  ) {
    // Legacy external collection: no charge line yet and no copay suggestion —
    // mint the charge and apply the payment in one call.
    return [
      {
        amount_cents: allocation.amountCents,
        charge_line_ids: null,
        encounter_id: scopedEncounterId,
        create_charge: true,
      },
    ];
  }

  return [
    {
      amount_cents: allocation.amountCents,
      charge_line_ids: null,
      encounter_id: isEncounterScope ? scopedEncounterId : null,
      create_charge: undefined,
    },
  ];
}

// ─── target labeling helpers ─────────────────────────────────────────────────

type PreviewLine = DisplayEncounterChargeGroup['lines'][number];

function chargeLineTitle(line: PreviewLine): string {
  const code = normalizeCode(line.code);
  return (
    line.responsibilityLabel ??
    normalizeDescription(line.description, code) ??
    (code ? `CPT ${code}` : 'Charge')
  );
}

function chargeLineSubtitle(
  group: DisplayEncounterChargeGroup,
  line: PreviewLine,
): string {
  const code = normalizeCode(line.code);
  const pieces = [
    group.serviceDate ? shortDate(group.serviceDate) : null,
    group.encounterTypeLabel,
    code && chargeLineTitle(line) !== `CPT ${code}` ? `CPT ${code}` : null,
  ].filter(Boolean);
  return pieces.join(' · ') || 'Open charge';
}

function serviceDateFor(
  groups: DisplayEncounterChargeGroup[],
  target: AllocationTarget,
): string | null {
  const group = groups.find(candidate => candidate.id === target.encounterId);
  return group?.serviceDate ?? null;
}

function normalizeCode(code: string | null | undefined): string | null {
  const normalized = code?.replace(/^CPT:/, '').replace(/^CPT\s+/, '').trim();
  return normalized || null;
}

function normalizeDescription(
  description: string | null | undefined,
  code: string | null,
): string | null {
  const normalized = description?.replace(/^CPT:/, '').replace(/^CPT\s+/, '').trim();
  if (!normalized || normalized === code) return null;
  return description ?? null;
}

function shortDate(date: string): string {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export const formatCheckedAtTime = (iso: string): string => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
};
